import { Dialog } from "@headlessui/react";
import { Loader2, X } from "lucide-react";
import { useState, useEffect, useMemo } from "react";
import { showErrorToast, showSuccessToast } from "../../utils/toast";
import api from "../../services/api";

export interface Equipment {
  equipment: {
    name: string;
    type: string;
    subtype: string;
    equipment_subtype_id?: number;
    equipment_type?: {
      id: number;
      name: string;
      subtypes?: Array<{
        id: number;
        name: string;
        equipment_type_id: number;
      }>;
    };
  };
  quantity: number;
}

interface GroupedEquipment {
  name: string;
  type: string;
  subtype: string;
  quantity: number;
  equipment_subtype_id: number;
}

interface StockRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  equipment: Equipment[];
  jobNumber: string;
  storeNumber: string;
  userEmail: string;
}

export default function StockRequestModal({
  isOpen,
  onClose,
  onSuccess,
  equipment,
  jobNumber,
  storeNumber,
  userEmail,
}: StockRequestModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [quantities, setQuantities] = useState<{ [key: string]: number }>({});

  // Memoize groupedEquipment calculation
  const groupedEquipment = useMemo(() => {
    return equipment.reduce((acc, item) => {
      const type = item.equipment.equipment_type?.name || "Unknown Type";
      const subtypeId = Number(item.equipment.equipment_subtype_id);

      // Find subtype by ID only, ignoring equipment_type_id
      const subtype =
        item.equipment.equipment_type?.subtypes?.find(
          (st) => Number(st.id) === subtypeId
        )?.name || "No Subtype";

      const key = `${type}-${subtype}`;

      if (!acc[key]) {
        acc[key] = {
          name: item.equipment.name,
          type: type,
          subtype: subtype,
          quantity: 1,
          equipment_subtype_id: subtypeId,
        };
      } else {
        acc[key].quantity += 1;
      }
      return acc;
    }, {} as { [key: string]: GroupedEquipment });
  }, [equipment]);

  // Update quantities when groupedEquipment changes
  useEffect(() => {
    const initial: { [key: string]: number } = {};
    Object.entries(groupedEquipment).forEach(([key, item]) => {
      initial[key] = item.quantity;
    });
    setQuantities(initial);
  }, [groupedEquipment, isOpen]);

  const handleQuantityChange = (key: string, value: number) => {
    setQuantities((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);

      const validItems = Object.entries(groupedEquipment)
        .filter(([key, item]) => {
          const subtypeId = Number(item.equipment_subtype_id);
          const isValid = quantities[key] > 0 && !isNaN(subtypeId);
          return isValid;
        })
        .map(([key, item]) => ({
          equipment_subtype_id: Number(item.equipment_subtype_id),
          quantity_requested: quantities[key],
        }));

      const requestData = {
        user_email: userEmail,
        jobcard_number: jobNumber,
        store_number: storeNumber,
        request_type: "van",
        items: validItems,
      };

      // Only proceed if there are valid items to submit
      if (requestData.items.length === 0) {
        showErrorToast(
          "No valid items to submit. Please ensure all items have a valid equipment subtype."
        );
        return;
      }

      // Send to local API
      await api.post("/stock-requests", requestData);
      showSuccessToast("Stock request submitted successfully");
      onSuccess?.();
      onClose();
    } catch (error) {
      showErrorToast("Error submitting stock request");
      console.error("Stock Request Error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      className="fixed inset-0 z-50 overflow-y-auto"
    >
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

        <Dialog.Panel className="relative bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl mx-4">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <X className="w-5 h-5" />
          </button>

          <Dialog.Title className="text-xl font-bold mb-4 text-gray-900 dark:text-white">
            Request Stock Replenishment
          </Dialog.Title>

          <div className="space-y-4">
            <p className="text-sm text-red-600 dark:text-red-400">
              Enter 0 quantity, for any equipment not required!
            </p>
            {Object.keys(groupedEquipment).length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-600 dark:text-gray-400">
                  No equipment was used on this job card.
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-500 mt-2">
                  Please use{" "}
                  <a
                    href="https://atera.colefaxtest.co.za/stock/requests/create"
                    target="_blank"
                    className="text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    Colefax Atera &nbsp;
                  </a>
                  to request stock replenishment.
                </p>
              </div>
            ) : (
              Object.entries(groupedEquipment).map(([key, item]) => (
                <div
                  key={key}
                  className="flex items-center justify-between p-4 border rounded-lg dark:border-gray-700"
                >
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900 dark:text-white">
                      {item.name}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Type: {item.type} | Subtype: {item.subtype}
                    </p>
                    <p className="text-sm text-blue-600 dark:text-blue-400">
                      Used: {item.quantity}
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <label className="text-sm text-gray-600 dark:text-gray-400">
                      Request Quantity:
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={quantities[key]}
                      onChange={(e) =>
                        handleQuantityChange(key, parseInt(e.target.value) || 0)
                      }
                      className="w-20 px-2 py-1 border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="mt-6 flex justify-end space-x-4">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
            >
              {Object.keys(groupedEquipment).length === 0 ? "Close" : "Cancel"}
            </button>
            {Object.keys(groupedEquipment).length > 0 && (
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-400 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Submitting...</span>
                  </>
                ) : (
                  <span>Submit Request</span>
                )}
              </button>
            )}
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
